// =============================================================================
// SIREPPAR - Textos precargados de los informes de la etapa de ATRIBUTOS
// =============================================================================
// Los tres informes de esta etapa siguen los modelos en papel de la SEP, así que
// el texto se arma acá (y no en el PDF) para que quede editable en el modal:
//
//   1. Inicio de trámite      → apertura formal del expediente (firma la Directora)
//   2. Elevación al Secretario → atributos verificados, listos para publicar
//   3. Publicación (Art. 10)   → el edicto que ordena el Secretario
//
// Los dos últimos NO llevan destinatario: el de elevación porque el modelo en papel
// no lo trae, y el de publicación porque se publica para todo el mundo.
// =============================================================================

interface PartidoInforme {
  denominacion: string;
  tipo?: string;
  localidad?: string | null;
}

interface IniciadorInforme {
  nombre?: string;
  apellido?: string;
  dni?: string;
  email?: string;
}

/**
 * Cómo se nombra al partido en los informes según su tipo. Espeja
 * obtenerTipoTextoPublicacion del backend (utils/pdfGenerator): para un MUNICIPAL hay
 * que decir de qué ciudad; para un PROVINCIAL alcanza el tipo.
 *
 * Si falta la localidad de un municipal se deja un placeholder visible en lugar de
 * inventar una ciudad: quien redacta lo ve y lo corrige antes de guardar.
 */
export function tipoPartidoTexto(partido?: PartidoInforme | null): string {
  if (!partido) return 'Partido Político';
  if (partido.tipo === 'MUNICIPAL') {
    const ciudad = partido.localidad?.trim();
    return `Partido Político Municipal de la ciudad de ${ciudad || '[COMPLETAR LOCALIDAD]'}`;
  }
  if (partido.tipo === 'PROVINCIAL') return 'Partido Político Provincial';
  return 'Partido Político';
}

/** "12 de marzo de 2026" — para las fórmulas "a la presentación de la fecha…". */
function fechaLarga(fecha: Date = new Date()): string {
  return fecha.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Informe de INICIO DE TRÁMITE. Nombra a quien presentó (con su DNI y su correo)
 * porque la apertura del expediente se hace en virtud de esa presentación.
 */
export function textoInformeInicioTramite(params: {
  numero: string;
  partido?: PartidoInforme | null;
  iniciador?: IniciadorInforme | null;
  // Los demás iniciadores declarados en el wizard externo. No tienen usuario en el
  // sistema, así que el informe de inicio es el acto que los acredita como
  // impulsores del expediente: por eso se los nombra uno por uno.
  iniciadoresAdicionales?: Array<{ nombre?: string; apellido?: string; dni?: string; email?: string }> | null;
}): string {
  const { numero, partido, iniciador, iniciadoresAdicionales } = params;
  const tipo = tipoPartidoTexto(partido);
  const describir = (p?: { nombre?: string; apellido?: string; dni?: string; email?: string } | null) => [
    [p?.nombre, p?.apellido].filter(Boolean).join(' ').trim() || null,
    p?.dni ? `DNI N° ${p.dni}` : null,
    p?.email || null,
  ].filter(Boolean).join(', ');

  const datosIniciador = describir(iniciador) || '[COMPLETAR NOMBRE DEL PRESENTANTE]';
  const otros = (iniciadoresAdicionales ?? []).map(describir).filter(Boolean);
  const bloqueOtros = otros.length > 0
    ? `\n\nSe declaran como iniciadores del trámite, además, a las siguientes personas:\n\n`
      + otros.map((d) => `- ${d}`).join('\n')
    : '';

  return (
    `Por medio de la presente se da inicio formal al trámite de reconocimiento y constitución `
    + `del ${tipo} con denominación provisoria: **${partido?.denominacion ?? ''}**, que tramita `
    + `bajo el expediente N° **${numero}**.\n\n`
    + `La apertura del expediente se realiza en virtud de la presentación efectuada por: `
    + `${datosIniciador}.`
    + bloqueOtros
  );
}

/**
 * Informe de ELEVACIÓN AL SECRETARIO de los atributos verificados. Sigue el modelo
 * en papel: informa la presentación, pide continuidad del trámite de reserva del
 * nombre e intima al solicitante por los Arts. 17, 18 y 19.
 *
 * El PDF agrega abajo el listado de atributos y anexa los símbolos, así que el texto
 * no los repite.
 */
export function textoInformeElevarSecretarioAtributos(params: {
  numero: string;
  partido?: PartidoInforme | null;
  atributosObservados?: string[];
}): string {
  const { numero, partido, atributosObservados } = params;
  const tipo = tipoPartidoTexto(partido);
  const observados = (atributosObservados ?? []).filter(Boolean);

  const parrafoAtributos = observados.length > 0
    ? `Se deja constancia de que los siguientes atributos NO fueron aprobados: `
      + `${observados.join(', ')}.`
    : `Los atributos declarados fueron verificados y **aprobados** por este Registro.`;

  return (
    `A la presentación de la fecha ${fechaLarga()}, se le informa al Secretario Electoral para `
    + `dar continuidad al trámite de reserva del nombre del ${tipo} con denominación provisoria: `
    + `**${partido?.denominacion ?? ''}**, que tramita bajo el expediente N° **${numero}**.\n\n`
    + `${parrafoAtributos}\n\n`
    + `Sin perjuicio de ello, se intima al solicitante a cumplir lo previsto en los Arts. 17, 18 `
    + `y 19 de la Ley XII N° 23. Se remitirá al domicilio electrónico el modelo de ficha de `
    + `solicitud prevista en el Art. 17 de la ley mencionada.`
  );
}

/**
 * Edicto de PUBLICACIÓN de los atributos (Art. 10), que ordena el Secretario.
 *
 * Cubre solo la apertura: el plazo de oposición de 5 días, la casilla donde se
 * observa y dónde se publica son parte FIJA del PDF (no se editan desde el modal
 * porque son la fórmula legal), y el listado de atributos con los símbolos lo agrega
 * el PDF a continuación.
 */
export function textoPublicacionAtributos(params: {
  numero: string;
  partido?: PartidoInforme | null;
}): string {
  const { numero, partido } = params;
  const tipo = tipoPartidoTexto(partido);
  return (
    `Se comunica en los términos del artículo 10 de la Ley XII N° 23, que se han iniciado `
    + `actuaciones para la creación de un ${tipo} con la denominación: `
    + `**"${partido?.denominacion ?? ''}"** y los atributos que se detallan a continuación.\n\n`
    + `El mismo tramita por expediente N° **${numero}** de esta Secretaría Electoral Permanente.`
  );
}
